import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type {
  ActivePackage,
  DefaultOfferFlowMode,
  NavLayoutMode,
  NotificationPreferences,
} from '@proffer/shared'
import {
  apiGet,
  apiPostPublic,
  AUTH_TOKEN_STORAGE_KEY,
} from '../config/api'

export type AuthUser = {
  id: string
  email: string
  aiRoleId: string | null
  aiRole: {
    id: string
    name: string
    description: string | null
    isActive: boolean
  } | null
  business_info: string | null
  logoUrl: string | null
  businessName: string | null
  vatNumber: string | null
  businessEmail: string | null
  address: string | null
  phone: string | null
  mobile: string | null
  gemiNumber: string | null
  postalCode: string | null
  city: string | null
  taxOffice: string | null
  businessActivity: string | null
  slug: string | null
  isPublic: boolean
  plan: 'FREE' | 'PRO'
  offersUsage: number
  maxFreeOffers: number
  subscriptionStatus: string | null
  /** Ενεργές συνδρομές πακέτων (slug + περίοδος) */
  activePackages: ActivePackage[]
  /** Υπολογισμένα δικαιώματα από ενεργά πακέτα + catalog */
  entitlements: string[]
  billingInterval: 'MONTHLY' | 'ANNUAL' | null
  billingTier: 'bronze' | 'silver' | 'gold' | null
  proEndsAt: string | null
  tierEndsAt: string | null
  proTrialEndsAt: string | null
  proTrialActive: boolean
  proTrialDaysRemaining: number | null
  termsAcceptedAt: string | null
  onboardingCompletedAt: string | null
  navLayout: NavLayoutMode
  defaultOfferFlow: DefaultOfferFlowMode
  notificationPreferences: NotificationPreferences
}

type TokenResponse = {
  access_token: string
  user: AuthUser
}

export type SignupResponse = {
  message: string
  email: string
  verificationEmailSent: boolean
}

type AuthContextValue = {
  user: AuthUser | null
  isReady: boolean
  isLoggedIn: boolean
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    business_info?: string,
    acceptTerms?: boolean,
    referralCode?: string,
  ) => Promise<SignupResponse>
  refreshUser: () => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isReady, setIsReady] = useState(false)

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    } catch {
      /* ignore */
    }
    setUser(null)
  }, [])

  useEffect(() => {
    const onSessionLost = () => {
      setUser(null)
    }
    window.addEventListener('proffer:auth-lost', onSessionLost)
    return () => window.removeEventListener('proffer:auth-lost', onSessionLost)
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      let token: string | null = null
      try {
        token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
      } catch {
        token = null
      }
      if (!token) {
        if (!cancelled) setIsReady(true)
        return
      }
      try {
        const me = await apiGet<AuthUser>('/auth/me')
        if (!cancelled) setUser(me)
      } catch {
        try {
          localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
        } catch {
          /* ignore */
        }
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setIsReady(true)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [])

  const persistSession = useCallback((data: TokenResponse) => {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, data.access_token)
    setUser(data.user)
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
      if (!token) {
        setUser(null)
        return
      }
      const me = await apiGet<AuthUser>('/auth/me')
      setUser(me)
    } catch {
      try {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      } catch {
        /* ignore */
      }
      setUser(null)
    }
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      } catch {
        /* ignore */
      }
      const data = await apiPostPublic<TokenResponse>('/auth/login', {
        email,
        password,
      })
      persistSession(data)
    },
    [persistSession],
  )

  const register = useCallback(
    async (
      email: string,
      password: string,
      business_info?: string,
      acceptTerms = false,
      referralCode?: string,
    ) => {
      try {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      } catch {
        /* ignore */
      }
      const body: Record<string, string | boolean> = {
        email,
        password,
        acceptTerms,
      }
      if (business_info?.trim()) {
        body.business_info = business_info.trim()
      }
      const ref = referralCode?.trim()
      if (ref) {
        body.referralCode = ref
      }
      return await apiPostPublic<SignupResponse>('/auth/signup', body)
    },
    [],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isReady,
      isLoggedIn: !!user,
      login,
      register,
      refreshUser,
      logout,
    }),
    [user, isReady, login, register, refreshUser, logout],
  )

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('Το useAuth πρέπει να χρησιμοποιείται μέσα σε AuthProvider.')
  }
  return ctx
}
